// Menu Icon Mapper - AgentBheem ERP
import {
  LayoutDashboard,
  Users,
  UserCog,
  UserCheck,
  Settings,
  Wallet,
  Package,
  TrendingUp,
  ShoppingCart,
  BarChart3,
  CalendarCheck,
  ClipboardList,
  FileText,
  FolderOpen,
  Mail,
  MessageSquare,
  Bell,
  Home,
  Building2,
  Briefcase,
  CreditCard,
  DollarSign,
  Receipt,
  Calculator,
  PieChart,
  Activity,
  Target,
  Layers,
  Grid3X3,
  ListTodo,
  Kanban,
  GanttChart,
  Clock,
  Calendar,
  Video,
  Presentation,
  StickyNote,
  Brain,
  Sparkles,
  Zap,
  Shield,
  Lock,
  Key,
  Globe,
  Link,
  ExternalLink,
  ChevronRight,
  ChevronDown,
  Circle,
  // Additional icons for menu groups
  Wrench,
  Megaphone,
  Workflow,
  UserCircle,
  Cog,
  BookOpen,
  Landmark,
  Factory,
  Store,
  Truck,
  HandCoins,
  BadgeDollarSign,
  FileSpreadsheet,
  FileCog,
  Server,
  Database,
  HardDrive,
  Monitor,
  Smartphone,
  Tablet,
  Network,
  Share2,
  GitBranch,
  Boxes,
  LayoutGrid,
  SquareStack,
  Blocks,
  Component,
  Puzzle,
  Award,
  Star,
  Heart,
  ThumbsUp,
  type LucideIcon,
} from 'lucide-react'

// Map backend icon names to Lucide components
const iconMap: Record<string, LucideIcon> = {
  // Dashboard & Home
  LayoutDashboard,
  Dashboard: LayoutDashboard,
  Home,

  // Users & HR
  Users,
  UserCog,
  UserCheck,
  UserCircle,
  User: Users,
  Employee: UserCheck,
  'Human Resource': Users,
  'Human Resources': Users,
  HumanResource: Users,
  HR: Users,
  People: UserCircle,
  Staff: Users,
  Team: Users,

  // Organization
  Building2,
  Organization: Building2,
  Company: Building2,
  Briefcase,
  'Core Business': Briefcase,
  CoreBusiness: Briefcase,
  Business: Briefcase,
  Enterprise: Building2,

  // Finance & Accounting
  Wallet,
  DollarSign,
  CreditCard,
  Receipt,
  Calculator,
  Finance: Wallet,
  Accounting: Calculator,
  Accounts: Calculator,
  Banking: Landmark,
  Landmark,
  HandCoins,
  BadgeDollarSign,
  Payroll: BadgeDollarSign,
  Salary: BadgeDollarSign,

  // Sales & Purchase
  TrendingUp,
  ShoppingCart,
  Sales: TrendingUp,
  Purchase: ShoppingCart,
  Store,
  Retail: Store,
  Commerce: ShoppingCart,

  // Inventory & Warehouse
  Package,
  Inventory: Package,
  Warehouse: Boxes,
  Boxes,
  Stock: Package,
  Truck,
  Logistics: Truck,
  Shipping: Truck,

  // Manufacturing
  Factory,
  Manufacturing: Factory,
  Production: Factory,

  // Reports & Analytics
  BarChart3,
  PieChart,
  Activity,
  Reports: BarChart3,
  Analytics: PieChart,
  Statistics: BarChart3,
  Charts: PieChart,

  // Project Management
  Target,
  Layers,
  Grid3X3,
  ListTodo,
  Kanban,
  GanttChart,
  Project: Target,
  Tasks: ListTodo,
  Projects: Target,
  Milestones: Target,

  // Calendar & Time
  CalendarCheck,
  Calendar,
  Clock,
  Attendance: CalendarCheck,
  Leave: Calendar,
  TimeTracking: Clock,

  // Documents
  ClipboardList,
  FileText,
  FolderOpen,
  Documents: FolderOpen,
  Files: FolderOpen,
  FileSpreadsheet,
  FileCog,
  Spreadsheet: FileSpreadsheet,

  // Communication
  Mail,
  MessageSquare,
  Bell,
  Video,
  Chat: MessageSquare,
  Meet: Video,
  Notifications: Bell,

  // Tools & Setup
  Wrench,
  Tools: Wrench,
  Utilities: Wrench,
  Presentation,
  StickyNote,
  Notes: StickyNote,

  // Setup & Configuration
  Settings,
  Cog,
  Setup: Cog,
  Configuration: Settings,
  Config: Settings,
  Preferences: Settings,
  System: Server,

  // Workflow & Process
  Workflow,
  'Aligner Workflow': Workflow,
  AlignerWorkflow: Workflow,
  Process: Workflow,
  Automation: Workflow,
  GitBranch,
  Branching: GitBranch,

  // Marketing
  Megaphone,
  'Digital Marketing': Megaphone,
  DigitalMarketing: Megaphone,
  Marketing: Megaphone,
  Campaign: Megaphone,
  Promotion: Megaphone,
  Advertising: Megaphone,

  // AI & Smart Features
  Brain,
  Sparkles,
  Zap,
  AI: Brain,
  Intelligence: Brain,
  Smart: Sparkles,

  // Security & Permissions
  Shield,
  Lock,
  Key,
  Permissions: Shield,
  Security: Shield,
  Access: Key,
  Roles: Shield,

  // Technology & Infrastructure
  Server,
  Database,
  HardDrive,
  Monitor,
  Smartphone,
  Tablet,
  Network,
  Infrastructure: Server,
  IT: Monitor,

  // Sharing & Integration
  Share2,
  Link,
  ExternalLink,
  Globe,
  Integration: Share2,
  API: Network,
  Connections: Network,

  // Layout & Components
  LayoutGrid,
  SquareStack,
  Blocks,
  Component,
  Puzzle,
  Modules: Blocks,
  Components: Component,
  Extensions: Puzzle,

  // Recognition & Feedback
  Award,
  Star,
  Heart,
  ThumbsUp,
  Achievements: Award,
  Rewards: Star,
  Favorites: Heart,

  // Learning & Documentation
  BookOpen,
  Training: BookOpen,
  Learning: BookOpen,
  Documentation: BookOpen,
  Help: BookOpen,
  Guide: BookOpen,

  // Navigation
  ChevronRight,
  ChevronDown,

  // Default
  Circle,
}

// Keyword-based icon fallbacks for better matching
const keywordIconMap: Array<{ keywords: string[]; icon: LucideIcon }> = [
  { keywords: ['account', 'finance', 'money', 'payment'], icon: Calculator },
  { keywords: ['workflow', 'flow', 'process', 'aligner'], icon: Workflow },
  { keywords: ['business', 'core', 'enterprise'], icon: Briefcase },
  { keywords: ['market', 'digital', 'campaign', 'promo'], icon: Megaphone },
  { keywords: ['human', 'resource', 'hr', 'employee'], icon: Users },
  { keywords: ['people', 'person', 'staff', 'team'], icon: UserCircle },
  { keywords: ['setup', 'config', 'setting', 'preference'], icon: Cog },
  { keywords: ['tool', 'utility', 'helper'], icon: Wrench },
  { keywords: ['report', 'analytic', 'chart', 'stat'], icon: BarChart3 },
  { keywords: ['project', 'task', 'todo'], icon: Target },
  { keywords: ['calendar', 'schedule', 'event'], icon: Calendar },
  { keywords: ['attend', 'time', 'clock'], icon: CalendarCheck },
  { keywords: ['leave', 'holiday', 'vacation'], icon: Calendar },
  { keywords: ['payroll', 'salary', 'wage'], icon: BadgeDollarSign },
  { keywords: ['recruit', 'hire', 'job', 'career'], icon: UserCheck },
  { keywords: ['document', 'file', 'folder'], icon: FolderOpen },
  { keywords: ['mail', 'email', 'message'], icon: Mail },
  { keywords: ['chat', 'conversation'], icon: MessageSquare },
  { keywords: ['video', 'meet', 'call'], icon: Video },
  { keywords: ['security', 'permission', 'access', 'role'], icon: Shield },
  { keywords: ['inventory', 'stock', 'warehouse'], icon: Package },
  { keywords: ['sale', 'order', 'customer'], icon: TrendingUp },
  { keywords: ['purchase', 'vendor', 'supplier'], icon: ShoppingCart },
  { keywords: ['dashboard', 'home', 'overview'], icon: LayoutDashboard },
  { keywords: ['ai', 'smart', 'intelligence', 'brain'], icon: Brain },
  { keywords: ['statutory', 'compliance', 'legal', 'tax'], icon: FileSpreadsheet },
  { keywords: ['training', 'learn', 'course'], icon: BookOpen },
]

interface MenuIconProps {
  name: string | null
  className?: string
}

export function MenuIcon({ name, className = 'h-5 w-5' }: MenuIconProps) {
  if (!name) {
    return <Briefcase className={className} />
  }

  // Try exact match first
  let Icon = iconMap[name]

  // Try case-insensitive match
  if (!Icon) {
    const lowerName = name.toLowerCase()
    const key = Object.keys(iconMap).find((k) => k.toLowerCase() === lowerName)
    if (key) Icon = iconMap[key]
  }

  // Try keyword-based matching
  if (!Icon) {
    const lowerName = name.toLowerCase()
    for (const { keywords, icon } of keywordIconMap) {
      if (keywords.some((kw) => lowerName.includes(kw))) {
        Icon = icon
        break
      }
    }
  }

  // Fallback to Briefcase (more meaningful than Circle)
  if (!Icon) {
    Icon = Briefcase
  }

  return <Icon className={className} />
}

export { ChevronRight, ChevronDown }
